import type { FormEvent } from "react";
import { useState } from "react";
import type { Boat } from "../../../../common/src";
import { rangeLengthDays } from "../../../../common/src";
import { navigate } from "../../hooks/useRoute";
import type { DaySelection } from "../../utils/selection";
import { Button } from "../shared/Button";
import { Card } from "../shared/Card";
import { Field } from "../shared/Field";
import { Notice } from "../shared/Notice";
import { useFormStyles } from "../shared/styles";

interface ReservationFormProps {
  boat: Boat;
  selection: DaySelection;
  onSelectionChange: (selection: DaySelection) => void;
  /** Party size carried over from a search. */
  initialPartySize?: number;
}

/** Pick dates and party size, then continue to sign the Boat Loan Agreement. */
export const ReservationForm = ({
  boat,
  selection,
  onSelectionChange,
  initialPartySize,
}: ReservationFormProps) => {
  const form = useFormStyles();
  const [partySize, setPartySize] = useState(String(initialPartySize ?? 1));

  const { startDate, endDate } = selection;
  const effectiveEnd = endDate ?? startDate;
  const selectedDays =
    startDate && effectiveEnd && effectiveEnd >= startDate
      ? rangeLengthDays({ startDate, endDate: effectiveEnd })
      : 0;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!startDate || !effectiveEnd) {
      return;
    }
    const party = Number(partySize);
    navigate({
      page: "reserve",
      boatId: boat.id,
      trip: {
        startDate,
        endDate: effectiveEnd,
        partySize: Number.isInteger(party) && party > 0 ? party : undefined,
      },
    });
  };

  return (
    <Card title="Reserve">
      <p className={form.intro}>
        Click an open day to start, then click your last day. Click the same day
        twice for a single day.
      </p>
      <form onSubmit={submit} noValidate>
        <div className={form.grid}>
          <Field label="First day" htmlFor="res-start">
            <input
              id="res-start"
              type="date"
              className={form.control}
              value={startDate ?? ""}
              onChange={(e) =>
                onSelectionChange({
                  startDate: e.target.value || undefined,
                  endDate,
                })
              }
            />
          </Field>
          <Field label="Last day" htmlFor="res-end">
            <input
              id="res-end"
              type="date"
              className={form.control}
              value={endDate ?? ""}
              min={startDate}
              onChange={(e) =>
                onSelectionChange({
                  startDate,
                  endDate: e.target.value || undefined,
                })
              }
            />
          </Field>
          <Field
            label="Party size"
            htmlFor="res-party"
            hint={`Including you. Up to ${boat.capacity}.`}
          >
            <input
              id="res-party"
              type="number"
              min={1}
              max={boat.capacity}
              className={form.control}
              value={partySize}
              onChange={(e) => setPartySize(e.target.value)}
            />
          </Field>
        </div>
        <div className={form.actions}>
          <Button type="submit" disabled={!startDate}>
            {selectedDays > 0
              ? `Continue: reserve ${selectedDays} day${selectedDays === 1 ? "" : "s"}`
              : "Continue"}
          </Button>
          {startDate && (
            <Button variant="ghost" onClick={() => onSelectionChange({})}>
              Clear
            </Button>
          )}
        </div>
        {selectedDays > boat.maxConsecutiveDays && (
          <Notice kind="info">{`This boat can be reserved for at most ${boat.maxConsecutiveDays} days in a row.`}</Notice>
        )}
      </form>
    </Card>
  );
};
